import { useState } from 'react';
import { useProjectStore } from '../../store/projectStore';
import { ApplicationForm } from './ApplicationForm';
import { TierList } from '../tiers/TierList';
import type { Application } from '../../types';

const s = {
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '16px',
  },
  headerText: { fontSize: '13px', color: '#8FA4B5', lineHeight: 1.5, flex: 1 },
  btnAdd: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#F5A621',
    border: '1px solid rgba(245,166,33,0.5)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  inlineFormBox: {
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '8px',
    backgroundColor: '#0d1f30',
    padding: '16px',
    marginBottom: '12px',
  },
  inlineFormTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#4C5C68',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.8px',
    marginBottom: '12px',
  },
  emptyState: {
    border: '2px dashed rgba(76,92,104,0.35)',
    borderRadius: '8px',
    padding: '48px 24px',
    textAlign: 'center' as const,
  },
  emptyTitle: { fontSize: '14px', fontWeight: 600, color: '#8FA4B5', marginBottom: '4px' },
  emptySub: { fontSize: '13px', color: '#4C5C68', marginBottom: '20px' },
  btnAddFirst: {
    padding: '9px 18px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '8px',
    backgroundColor: '#0d1f30',
    padding: '12px 14px',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  cardHover: {
    borderColor: 'rgba(245,166,33,0.5)',
  },
  cardLeft: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  icon: {
    width: '28px',
    height: '28px',
    backgroundColor: '#0a1929',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    flexShrink: 0,
    marginTop: '1px',
  },
  cardName: { fontSize: '14px', fontWeight: 600, color: '#E8EDF2', marginBottom: '2px' },
  cardDesc: { fontSize: '12px', color: '#8FA4B5', marginBottom: '2px' },
  cardMeta: { fontSize: '11px', color: '#4C5C68' },
  cardActions: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, marginLeft: '12px' },
  iconBtn: {
    padding: '5px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#8FA4B5',
  },
  confirmRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  confirmLabel: { fontSize: '12px', color: '#8FA4B5' },
  btnYes: {
    padding: '4px 10px',
    backgroundColor: 'rgba(220,60,60,0.15)',
    color: '#E05555',
    border: '1px solid rgba(220,60,60,0.3)',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  btnNo: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: '24px' },
  btnBack: {
    padding: '9px 16px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnNext: {
    padding: '9px 20px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '7px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnNextDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
};

type EditingState = { mode: 'none' } | { mode: 'add' } | { mode: 'edit'; id: string };

interface ApplicationListProps {
  onNext: () => void;
  onBack: () => void;
}

export function ApplicationList({ onNext, onBack }: ApplicationListProps) {
  const project = useProjectStore(st => st.project);
  const addApplication = useProjectStore(st => st.addApplication);
  const updateApplication = useProjectStore(st => st.updateApplication);
  const deleteApplication = useProjectStore(st => st.deleteApplication);

  const [editing, setEditing] = useState<EditingState>({ mode: 'none' });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (!project) return null;

  const apps = project.applications;

  function handleAdd(name: string, description?: string) {
    addApplication(name, description);
    setEditing({ mode: 'none' });
  }

  function handleEdit(app: Application, name: string, description?: string) {
    updateApplication(app.id, { name, description });
    setEditing({ mode: 'none' });
  }

  function handleDelete(id: string) {
    deleteApplication(id);
    setConfirmDeleteId(null);
  }

  return (
    <div style={{ maxWidth: '640px' }}>
      {/* Header */}
      <div style={s.header}>
        <p style={s.headerText}>
          Group your virtual machines into logical application sets.
          Each group will appear in the VM inventory on the next step.
        </p>
        {editing.mode === 'none' && (
          <button type="button" style={s.btnAdd} onClick={() => setEditing({ mode: 'add' })}>
            + Add Application
          </button>
        )}
      </div>

      {/* Add form */}
      {editing.mode === 'add' && (
        <div style={s.inlineFormBox}>
          <p style={s.inlineFormTitle}>New Application Group</p>
          <ApplicationForm
            onSave={handleAdd}
            onCancel={() => setEditing({ mode: 'none' })}
            submitLabel="Add Application"
          />
        </div>
      )}

      {/* Empty state */}
      {apps.length === 0 && editing.mode === 'none' && (
        <div style={s.emptyState}>
          <p style={s.emptyTitle}>No application groups yet</p>
          <p style={s.emptySub}>Add at least one group to continue</p>
          <button type="button" style={s.btnAddFirst} onClick={() => setEditing({ mode: 'add' })}>
            + Add First Application
          </button>
        </div>
      )}

      {/* List */}
      <ul style={s.list}>
        {apps.map(app => (
          <li key={app.id}>
            {editing.mode === 'edit' && editing.id === app.id ? (
              <div style={{ ...s.inlineFormBox, marginBottom: '8px' }}>
                <p style={s.inlineFormTitle}>Edit Application Group</p>
                <ApplicationForm
                  initialName={app.name}
                  initialDescription={app.description ?? ''}
                  onSave={(name, description) => handleEdit(app, name, description)}
                  onCancel={() => setEditing({ mode: 'none' })}
                  submitLabel="Save Changes"
                />
              </div>
            ) : (
              <div
                style={{
                  ...s.card,
                  ...(hoveredId === app.id ? s.cardHover : {}),
                }}
                onClick={() => setEditing({ mode: 'edit', id: app.id })}
                onMouseEnter={() => setHoveredId(app.id)}
                onMouseLeave={() => setHoveredId(null)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') setEditing({ mode: 'edit', id: app.id }); }}
                aria-label={`Edit ${app.name}`}
              >
                <div style={s.cardLeft}>
                  <div style={s.icon}>⬡</div>
                  <div>
                    <p style={s.cardName}>{app.name}</p>
                    {app.description && <p style={s.cardDesc}>{app.description}</p>}
                    <p style={s.cardMeta}>
                      {app.vms.length === 0 ? 'No VMs yet' : `${app.vms.length} VM${app.vms.length !== 1 ? 's' : ''}`}
                      {' · '}
                      {app.tiers.length} tier{app.tiers.length !== 1 ? 's' : ''}
                    </p>
                    <div onClick={e => e.stopPropagation()} role="presentation">
                      <TierList applicationId={app.id} />
                    </div>
                  </div>
                </div>

                <div
                  style={s.cardActions}
                  onClick={e => e.stopPropagation()}
                  role="presentation"
                >
                  {confirmDeleteId === app.id ? (
                    <div style={s.confirmRow}>
                      <span style={s.confirmLabel}>Delete?</span>
                      <button type="button" style={s.btnYes} onClick={() => handleDelete(app.id)}>Yes</button>
                      <button type="button" style={s.btnNo} onClick={() => setConfirmDeleteId(null)}>No</button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        title="Delete"
                        style={{ ...s.iconBtn, color: '#E05555' }}
                        onClick={() => setConfirmDeleteId(app.id)}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Navigation */}
      <div style={s.nav}>
        <button type="button" style={s.btnBack} onClick={onBack}>← Back</button>
        <button
          type="button"
          disabled={apps.length === 0}
          style={{ ...s.btnNext, ...(apps.length === 0 ? s.btnNextDisabled : {}) }}
          data-primary-cta
          onClick={onNext}
        >
          Next: Virtual Machines →
        </button>
      </div>
    </div>
  );
}
